import type {
    TicketCancelPayload,
    TicketCompletePayload,
    TicketPayload,
    TicketResponse,
} from '@/types/apps/ticket.type';
import type {
    ApiResponse,
    PaginatedResult,
    QueryParams,
} from '@/types/common/api.type';
import type { BaseApiClientInterface } from '@/types/service/base-api-client.interface';
import type { Creatable } from '@/types/service/creatable.interface';
import type { Deletable } from '@/types/service/deletable.interface';
import type { Indexable } from '@/types/service/indexable.interface';
import type { Showable } from '@/types/service/showable.interface';
import type { Updatable } from '@/types/service/updatable.interface';

type TicketListResponse = ApiResponse<PaginatedResult<TicketResponse>>;

export class TicketService
    implements Showable, Updatable, Deletable, Creatable, Indexable
{
    public constructor(private apiClient: BaseApiClientInterface) {}

    public endpoints = {
        base: () => '/api/ticket',
        detail: (id: string) => `/api/ticket/${id}`,
        restore: () => '/api/ticket/restore',
        approve: (id: string) => `/api/ticket/approve-ticket/${id}`,
        complete: (id: string) => `/api/ticket/complete-ticket/${id}`,
        cancel: (id: string) => `/api/ticket/cancel-ticket/${id}`,
    };

    public index(params?: QueryParams): Promise<TicketListResponse> {
        return this.apiClient.fetchApi(this.endpoints.base(), params);
    }

    public detail(
        id: string,
        params?: QueryParams
    ): Promise<ApiResponse<TicketResponse>> {
        return this.apiClient.fetchApi(this.endpoints.detail(id), params);
    }

    public create(
        payload: TicketPayload
    ): Promise<ApiResponse<TicketResponse>> {
        return this.apiClient.postApi(this.endpoints.base(), payload);
    }

    public update(
        id: string,
        payload: TicketPayload
    ): Promise<ApiResponse<TicketResponse>> {
        return this.apiClient.putApi(this.endpoints.detail(id), payload);
    }

    public restore(ids: string[]): Promise<ApiResponse<TicketResponse[]>> {
        return this.apiClient.putApi(this.endpoints.restore(), ids);
    }

    public approve(id: string): Promise<ApiResponse<TicketResponse>> {
        return this.apiClient.putApi(this.endpoints.approve(id), {});
    }

    public complete(
        id: string,
        payload: TicketCompletePayload
    ): Promise<ApiResponse<TicketResponse>> {
        return this.apiClient.putApi(this.endpoints.complete(id), payload);
    }

    public cancel(
        id: string,
        payload: TicketCancelPayload
    ): Promise<ApiResponse<TicketResponse>> {
        return this.apiClient.putApi(this.endpoints.cancel(id), payload);
    }

    public destroy(
        ids: string[],
        isHardDeleted: boolean = false
    ): Promise<ApiResponse<void>> {
        return this.apiClient.destroyApi(
            this.endpoints.base(),
            ids,
            isHardDeleted
        );
    }
}
